import { Alert } from 'src/design/atoms/Alert';
import { Card } from 'src/design/atoms/Card';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { Tabs } from 'src/design/molecules/Tabs';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { useFormik } from 'formik';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  OrganizationRoleDto,
  OrganizationRoleRequestDto,
} from 'src/api/FiretrolApiClient';
import {
  useCreateRole,
  useUpdateRole,
} from 'src/api/queries/organization/useRoleMutations';
import { ScopePicker } from 'src/routes/Settings/Roles/components/RoleForm/ScopePicker';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorSingle';
import { Scope } from 'src/data/scopes';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import {
  applyValidationErrors,
  getErrorMessage,
  logger,
  showErrorToast,
} from 'src/utils';

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(1, 'Name Required').label('Name'),
  description: Yup.string().required().min(1, 'Description Required'),
  scopes: Yup.array().required().min(1, 'At least one scope required'),
  daily_audit_tracking: Yup.boolean().required().default(false),
});

const defaultValues: OrganizationRoleRequestDto = {
  name: '',
  scopes: [],
  office: '',
  dailyAuditTracking: false,
  serviceJobTracking: false,
  description: '',
};

interface RoleFormProps {
  initialValues?: OrganizationRoleDto;
  listPath?: string;
}

export const RoleForm: React.FC<RoleFormProps> = ({
  initialValues,
  listPath = '/settings/roles',
}) => {
  const navigate = useNavigate();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const isEditing = !!initialValues?.id;

  const formik = useFormik({
    initialValues: initialValues ?? defaultValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      setSubmitting(true);
      try {
        if (isEditing && initialValues) {
          await updateRole.mutateAsync({
            id: initialValues.id,
            request: values,
          });
        } else {
          const result = await createRole.mutateAsync(values);
          navigate(`/settings/roles/edit/${result.id}`);
        }
      } catch (err: unknown) {
        logger.error('Failed to save role', { err });
        const { applied, unmatchedMessages } = applyValidationErrors(
          err,
          Object.keys(values),
          setFieldError,
        );
        if (applied && unmatchedMessages.length === 0) {
          showErrorToast('Please fix the highlighted fields below.');
        } else {
          showErrorToast(getErrorMessage(err));
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const onScopeChange = React.useMemo(
    () => (selectedScopes: Scope[]) => {
      return formik.setFieldValue('scopes', selectedScopes);
    },
    [formik.setFieldValue],
  );

  return (
    <Stack>
      <Group justify="flex-end" mb="md">
        <SecondaryButton onClick={() => navigate(listPath)}>
          Cancel
        </SecondaryButton>
      </Group>
      <Card>
        <Tabs defaultValue="details" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="details">Role Details</Tabs.Tab>
            <Tabs.Tab value="scopes">Scopes</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details">
            <Stack mt="md" maw={600}>
              <Input.Wrapper label="Office" description="Leave blank if global">
                <OfficeSelector
                  initialValue={formik.values.office}
                  onChange={(office) => formik.setFieldValue('office', office)}
                />
              </Input.Wrapper>

              <TextInput
                label="Role Name"
                placeholder="What do you want to call this role?"
                withAsterisk
                value={formik.values.name}
                onChange={(e) => formik.setFieldValue('name', e.target.value)}
                error={formik.touched.name && formik.errors.name}
              />

              <Textarea
                label="Description"
                placeholder="What is this role used for?"
                withAsterisk
                value={formik.values.description}
                onChange={(e) =>
                  formik.setFieldValue('description', e.target.value)
                }
                autosize
                minRows={3}
                error={formik.touched.description && formik.errors.description}
              />
              <Checkbox
                label="Track Daily Submissions"
                description="Users with this role will appear on the daily submission report and receive daily submission notifications."
                checked={formik.values.dailyAuditTracking}
                onChange={(e) =>
                  formik.setFieldValue('dailyAuditTracking', e.target.checked)
                }
              />
              <Checkbox
                label="Service Job Tracking"
                description="Users with this role will have service jobs assigned to them for tracking service work outside of CTC jobs."
                checked={formik.values.serviceJobTracking}
                onChange={(e) =>
                  formik.setFieldValue('serviceJobTracking', e.target.checked)
                }
              />
              {formik.values.serviceJobTracking && !formik.values.office && (
                <Alert color="yellow">
                  Service job tracking will apply to all offices that users with
                  this role are a part of. To limit service job tracking to a
                  specific office, select an office above.
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="scopes">
            <Stack mt="md">
              <Input.Wrapper label="Select Scopes" withAsterisk>
                <ScopePicker
                  hideLabel
                  onChange={onScopeChange}
                  initialSelectedScopes={
                    (formik.values.scopes ?? []) as Scope[]
                  }
                />
              </Input.Wrapper>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Card>

      <Group mt="md">
        {isEditing ? (
          <SaveButton
            onClick={() => formik.handleSubmit()}
            loading={formik.isSubmitting}
            disabled={!formik.isValid || !formik.dirty}
          >
            Save Changes
          </SaveButton>
        ) : (
          <CreateButton
            onClick={() => formik.handleSubmit()}
            loading={formik.isSubmitting}
            disabled={!formik.isValid || !formik.dirty}
          >
            Create Role
          </CreateButton>
        )}
      </Group>
    </Stack>
  );
};
